import { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import FloatingCTA from '@/components/feature/FloatingCTA';
import SeoHead from '@/components/feature/SeoHead';
import { servicesList } from '@/mocks/services';
import ServiceReviews from './components/ServiceReviews';

const SITE_URL = import.meta.env.VITE_SITE_URL || 'https://vanpolattesisat.biz';

const SERVICE_TITLES: Record<number, string> = {
  1: 'Sıhhi Tesisat',
  2: 'Tamirat & Tadilat İşleri',
  3: 'Kombi Bakımı & Petek Temizliği',
  4: 'Doğalgaz İç Tesisat',
  5: 'Kanal Açma & Tıkanıklık Açma',
  6: 'Musluk & Armatür Tamiratı',
  7: 'Arıza Tespiti & Cihaz Onarımı',
  8: 'Kooperatif & Site İşleri',
  9: 'Yangın Sistemleri',
};

const SERVICE_LONG_DESC: Record<number, string> = {
  1: 'Van Polat Tesisat olarak ev ve işyerlerinizde eksiksiz sıhhi tesisat hizmetleri sunuyoruz. Boru döşemeden su kaçağı tespitine, yeni kurulumdan eski tesisat yenilemesine kadar tüm su tesisatı işlemlerini 15 yılı aşkın deneyimimizle Van\'da güvenle yapıyoruz.',
  2: 'Mutfak, banyo ve tüm iç mekânlarda tamirat ve tadilat işlerini profesyonel ekibimizle gerçekleştiriyoruz. Musluk değişiminden komple banyo tadilatına, küçük onarımlardan büyük yenileme projelerine kadar garantili ve titiz işçilik sunuyoruz.',
  3: 'Kışa hazırlık için kombi bakımı ve petek temizliği hizmetimizle ısıtma sisteminizin verimini maksimuma çıkarıyoruz. Yıllık bakım, gaz kontrolü, basınç ayarı ve petek havalandırması ile kış boyunca sıcak ve ekonomik bir ev sağlıyoruz.',
  4: 'Belgeli doğalgaz iç tesisat ustalarımız Van\'da tüm doğalgaz kurulum ve onarım işlemlerini eksiksiz yapıyor. Güvenli gaz kaçağı tespiti, boru montajı ve resmi belgelendirme ile güvenliğinizi en ön planda tutuyoruz.',
  5: 'Lavabo, tuvalet, mutfak gideri ve ana hat tıkanıklıklarını profesyonel makineler ve yüksek basınçlı hydro-jetting yöntemiyle hızla açıyoruz. Acil durumlarda 7/24 Van ve çevre ilçelerde müdahale ediyoruz.',
  6: 'Akan musluklar, sızdıran bataryalar veya düşük su basıncı için aynı gün hizmet veriyoruz. Tüm marka ve model musluk ile armatürlerde tamir ve değişim yapıyor, su israfını önlüyoruz.',
  7: 'Termal kamera ve akustik dedektörlerle duvarı açmadan gizli su kaçaklarını tespit ediyoruz. Tahribatsız yöntemlerimiz sayesinde evinizde minimum hasar ile maksimum doğrulukta arıza tespiti yapılır.',
  8: 'Apartman ve site yöneticilerine özel periyodik bakım sözleşmeleri sunuyoruz. Ortak alan tesisat bakımından büyük çaplı yenileme projelerine kadar tüm bina tesisat ihtiyaçlarını profesyonel ekibimizle karşılıyoruz.',
  9: 'Konut ve ticari binalarda sprinkler sistemi kurulumu, yangın dolapları montajı ve periyodik bakımını gerçekleştiriyoruz. İtfaiye ve sigorta uyumlu belgelendirme ile bina güvenliğinizi yasal standartlara taşıyoruz.',
};

export default function HizmetDetay() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const service = servicesList.find((s) => s.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!service) navigate('/hizmetler', { replace: true });
  }, [slug, service, navigate]);

  if (!service) return null;

  const title = SERVICE_TITLES[service.id];
  const longDesc = SERVICE_LONG_DESC[service.id];
  const pageUrl = `${SITE_URL}/hizmetler/${service.slug}`;

  const avgRating =
    service.reviews.reduce((sum, r) => sum + r.rating, 0) / service.reviews.length;
  const roundedAvg = Math.round(avgRating * 10) / 10;

  const schema = [
    {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Ana Sayfa', item: SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Hizmetler', item: `${SITE_URL}/hizmetler` },
        { '@type': 'ListItem', position: 3, name: title, item: pageUrl },
      ],
    },
    {
      '@context': 'https://schema.org',
      '@type': 'Service',
      name: `${title} - Van`,
      description: longDesc,
      url: pageUrl,
      provider: {
        '@type': 'LocalBusiness',
        name: 'Van Polat Tesisat',
        telephone: '+905076074165',
        url: SITE_URL,
        address: {
          '@type': 'PostalAddress',
          addressLocality: 'Van',
          addressRegion: 'Van',
          addressCountry: 'TR',
        },
      },
      areaServed: { '@type': 'City', name: 'Van' },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: roundedAvg.toFixed(1),
        reviewCount: service.reviews.length,
        bestRating: '5',
        worstRating: '1',
      },
      review: service.reviews.map((r) => ({
        '@type': 'Review',
        author: { '@type': 'Person', name: r.author },
        datePublished: r.date,
        reviewRating: {
          '@type': 'Rating',
          ratingValue: r.rating,
          bestRating: '5',
        },
        reviewBody: r.text,
      })),
    },
    {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: service.faq.map((f) => ({
        '@type': 'Question',
        name: f.q,
        acceptedAnswer: { '@type': 'Answer', text: f.a },
      })),
    },
  ];

  const otherServices = servicesList.filter((s) => s.id !== service.id).slice(0, 3);

  return (
    <div className="min-h-screen flex flex-col">
      <SeoHead
        title={`${title} Van | Van Polat Tesisat`}
        description={longDesc}
        keywords={service.keywords}
        canonical={`/hizmetler/${service.slug}`}
        schema={schema}
      />
      <Navbar />

      {/* ── Hero ── */}
      <section className="relative pt-20 md:pt-24 h-64 md:h-80 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={service.heroImage}
            alt={`${title} Van`}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-blue-900/90 via-blue-800/80 to-blue-700/60" />
        </div>
        <div className="relative h-full flex flex-col justify-center max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-center gap-2 text-blue-200 text-xs mb-3 flex-wrap">
            <Link to="/" className="hover:text-white transition-colors cursor-pointer">Ana Sayfa</Link>
            <i className="ri-arrow-right-s-line"></i>
            <Link to="/hizmetler" className="hover:text-white transition-colors cursor-pointer">Hizmetler</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-white">{title}</span>
          </div>
          <div className="flex items-center gap-3 mb-3">
            <span className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-xl">
              <i className={`${service.icon} text-white text-xl`}></i>
            </span>
            <h1 className="text-2xl md:text-4xl font-extrabold text-white leading-tight" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {title} <span className="text-blue-200">Van</span>
            </h1>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-blue-100 text-sm">Van Polat Tesisat · 7/24 Hizmet · Garantili İşçilik</p>
            {/* Rating badge in hero */}
            <div className="flex items-center gap-1.5 bg-amber-400/20 border border-amber-300/40 rounded-full px-3 py-1">
              <i className="ri-star-fill text-amber-400 text-xs"></i>
              <span className="text-white text-xs font-bold">{roundedAvg.toFixed(1)}</span>
              <span className="text-blue-200 text-xs">({service.reviews.length} yorum)</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Main Content ── */}
      <main className="flex-1 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-8 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

            {/* ── Left: Content ── */}
            <div className="lg:col-span-2 space-y-8">

              {/* About section */}
              <section className="bg-white rounded-2xl p-6 md:p-8">
                <h2 className="text-lg md:text-2xl font-extrabold text-gray-900 mb-4" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Van&apos;da <strong className="text-blue-600">{title}</strong> Hizmeti
                </h2>
                <p className="text-gray-600 text-sm md:text-base leading-relaxed">{longDesc}</p>
              </section>

              {/* Features */}
              <section className="bg-white rounded-2xl p-6 md:p-8">
                <h2 className="text-base md:text-xl font-extrabold text-gray-900 mb-5" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Hizmet Kapsamı
                </h2>
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {service.features.map((feat, i) => (
                    <li key={i} className="flex items-start gap-3 bg-blue-50 rounded-xl p-3">
                      <span className="w-7 h-7 shrink-0 flex items-center justify-center bg-blue-600 rounded-lg mt-0.5">
                        <i className="ri-check-line text-white text-sm"></i>
                      </span>
                      <span className="text-gray-800 text-sm font-medium">{feat}</span>
                    </li>
                  ))}
                </ul>
              </section>

              {/* Why us */}
              <section className="bg-white rounded-2xl p-6 md:p-8">
                <h2 className="text-base md:text-xl font-extrabold text-gray-900 mb-5" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Neden Van Polat Tesisat?
                </h2>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { icon: 'ri-medal-line', title: '15+ Yıl Deneyim', desc: 'Van\'da yıllardır güvenilir hizmet' },
                    { icon: 'ri-shield-check-line', title: 'Garantili İşçilik', desc: 'Sorun çıkarsa ücretsiz müdahale' },
                    { icon: 'ri-time-line', title: 'Hızlı Müdahale', desc: 'Van içi aynı gün hizmet' },
                    { icon: 'ri-money-cny-circle-line', title: 'Şeffaf Fiyat', desc: 'Gizli ücret, sürpriz fatura yok' },
                  ].map((item, i) => (
                    <div key={i} className="flex items-start gap-3 border border-gray-100 rounded-xl p-3">
                      <span className="w-9 h-9 shrink-0 flex items-center justify-center bg-blue-50 rounded-xl">
                        <i className={`${item.icon} text-blue-600 text-lg`}></i>
                      </span>
                      <div>
                        <p className="font-bold text-gray-900 text-xs mb-0.5">{item.title}</p>
                        <p className="text-gray-500 text-xs">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* FAQ */}
              <section className="bg-white rounded-2xl p-6 md:p-8">
                <h2 className="text-base md:text-xl font-extrabold text-gray-900 mb-5" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Sık Sorulan Sorular
                </h2>
                <div className="space-y-3">
                  {service.faq.map((item, i) => (
                    <div key={i} className="border border-gray-100 rounded-xl overflow-hidden">
                      <button
                        className="w-full flex items-center justify-between gap-3 px-4 py-4 text-left bg-white hover:bg-blue-50 transition-colors cursor-pointer"
                        onClick={() => setOpenFaq(openFaq === i ? null : i)}
                      >
                        <span className="font-semibold text-gray-900 text-sm">{item.q}</span>
                        <span className="w-6 h-6 shrink-0 flex items-center justify-center">
                          <i className={`${openFaq === i ? 'ri-subtract-line' : 'ri-add-line'} text-blue-600 text-base`}></i>
                        </span>
                      </button>
                      {openFaq === i && (
                        <div className="px-4 pb-4 text-gray-600 text-sm leading-relaxed border-t border-gray-50 pt-3">
                          {item.a}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* ── Reviews ── */}
              <ServiceReviews reviews={service.reviews} serviceName={title} />

            </div>

            {/* ── Right: Sidebar ── */}
            <div className="space-y-5">

              {/* CTA Card */}
              <div className="bg-blue-600 rounded-2xl p-5 text-white sticky top-4">
                <p className="font-extrabold text-base mb-1" style={{ fontFamily: 'Poppins, sans-serif' }}>
                  Hemen Yardım Alın
                </p>
                <p className="text-blue-100 text-xs mb-5">Van ve çevre ilçelerde aynı gün hizmet</p>
                <div className="space-y-2.5">
                  <a
                    href="tel:+905076074165"
                    className="flex items-center justify-center gap-2 bg-white text-blue-700 font-extrabold text-sm py-3.5 rounded-xl transition-all cursor-pointer whitespace-nowrap w-full"
                  >
                    <i className="ri-phone-fill text-base"></i>
                    0507 607 4165
                  </a>
                  <a
                    href={service.waUrl}
                    target="_blank"
                    rel="nofollow noreferrer"
                    className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-extrabold text-sm py-3.5 rounded-xl transition-all cursor-pointer whitespace-nowrap w-full"
                  >
                    <i className="ri-whatsapp-line text-base"></i>
                    WhatsApp ile Yaz
                  </a>
                  <Link
                    to="/randevu"
                    className="flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-800 border border-blue-500 text-white font-extrabold text-sm py-3.5 rounded-xl transition-all cursor-pointer whitespace-nowrap w-full"
                  >
                    <i className="ri-calendar-check-line text-base"></i>
                    Randevu Al
                  </Link>
                </div>
              </div>

              {/* Rating summary in sidebar */}
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-5">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-3">Müşteri Memnuniyeti</p>
                <div className="flex items-center gap-3">
                  <p className="text-4xl font-extrabold text-amber-500">{roundedAvg.toFixed(1)}</p>
                  <div>
                    <div className="flex items-center gap-0.5 mb-1">
                      {Array.from({ length: 5 }, (_, i) => (
                        <i key={i} className={`${i < Math.round(roundedAvg) ? 'ri-star-fill text-amber-400' : 'ri-star-line text-gray-300'} text-base`} />
                      ))}
                    </div>
                    <p className="text-xs text-gray-500">{service.reviews.length} doğrulanmış yorum</p>
                  </div>
                </div>
              </div>

              {/* Working hours */}
              <div className="bg-white rounded-2xl p-5 border border-gray-100">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">Çalışma Saatleri</p>
                <ul className="space-y-2">
                  {[
                    { day: 'Pzt – Cuma', time: '07:00 – 22:00' },
                    { day: 'Cumartesi', time: '08:00 – 20:00' },
                    { day: 'Pazar', time: '09:00 – 18:00' },
                    { day: 'Acil 7/24', time: 'WhatsApp' },
                  ].map((r) => (
                    <li key={r.day} className="flex items-center justify-between text-xs">
                      <span className="text-gray-600">{r.day}</span>
                      <span className="font-semibold text-gray-900">{r.time}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Other services */}
              <div className="bg-white rounded-2xl p-5 border border-gray-100">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-4">Diğer Hizmetlerimiz</p>
                <div className="space-y-2">
                  {otherServices.map((s) => (
                    <Link
                      key={s.id}
                      to={`/hizmetler/${s.slug}`}
                      className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-blue-50 transition-colors cursor-pointer group"
                    >
                      <span className="w-8 h-8 flex items-center justify-center bg-blue-50 group-hover:bg-blue-100 rounded-lg shrink-0 transition-colors">
                        <i className={`${s.icon} text-blue-600 text-sm`}></i>
                      </span>
                      <span className="text-gray-800 text-xs font-medium">{SERVICE_TITLES[s.id]}</span>
                      <i className="ri-arrow-right-s-line text-gray-400 ml-auto"></i>
                    </Link>
                  ))}
                  <Link
                    to="/hizmetler"
                    className="flex items-center justify-center gap-2 mt-2 text-blue-600 text-xs font-bold hover:text-blue-800 transition-colors cursor-pointer"
                  >
                    Tüm Hizmetleri Gör <i className="ri-arrow-right-s-line"></i>
                  </Link>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* ── Bottom CTA ── */}
        <section className="bg-blue-900 py-10 md:py-14">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <h2 className="text-xl md:text-3xl font-extrabold text-white mb-3" style={{ fontFamily: 'Poppins, sans-serif' }}>
              {title} için Hemen Ulaşın
            </h2>
            <p className="text-blue-200 text-sm mb-7">
              Van ve çevre ilçelerde profesyonel tesisat hizmeti için arayın veya WhatsApp&apos;tan yazın.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-3">
              <a
                href="tel:+905076074165"
                className="flex items-center justify-center gap-2 bg-white text-blue-800 hover:bg-blue-50 font-extrabold text-sm px-8 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-phone-fill text-lg"></i>
                0507 607 4165
              </a>
              <a
                href={service.waUrl}
                target="_blank"
                rel="nofollow noreferrer"
                className="flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white font-extrabold text-sm px-8 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line text-lg"></i>
                WhatsApp ile Yaz
              </a>
              <Link
                to="/randevu"
                className="flex items-center justify-center gap-2 bg-blue-700 hover:bg-blue-600 border border-blue-500 text-white font-extrabold text-sm px-8 py-4 rounded-xl transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-calendar-check-line text-lg"></i>
                Randevu Al
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <FloatingCTA />
    </div>
  );
}
